import { useState } from "react"
import type {
  PartnershipCredentialKind,
  PartnershipDiscoveryStatus,
  PartnershipLastSyncDto,
} from "~/ui-rtk/api/types/partnerships.types"
import { cn } from "~/ui-rtk/utils/tailwind-utils"
import {
  BUSINESS_MANAGER_PANEL_ID,
  discoveryCredentialSuffix,
  explainSyncWarning,
  formatDiscoveryLine,
  isInformationalSyncWarning,
  isMetaTransientSyncWarning,
  isNoAgencyCredentialSkip,
  splitMetaNote,
} from "./partnership-copy"

/**
 * Sync health (design §1.3): what the last Meta sync managed to read, per
 * platform, plus every warning it raised translated into a fix. Sits above the
 * table so a wrong Business Manager id, a lost asset permission or a missing
 * scope is the first thing on screen instead of a trailing "; 3 warnings".
 *
 * Amber only for something actionable: Instagram discovery not `ok`, Facebook
 * discovery `unavailable` (an actual refusal), or a real warning. A Facebook
 * `skipped` is the normal state for a client-connected brand (Meta never
 * grants that scope to them) and stays informational, so the healthy blue
 * state is reachable.
 *
 * Renders nothing while a sync is running (the header shows that) and nothing
 * for older COMPLETED runs that carry neither discovery outcomes nor warnings.
 */

/** Above this many warnings the list collapses to one amber line. */
export const COLLAPSE_WARNINGS_ABOVE = 3

export interface SyncHealthPanelProps {
  lastSync: PartnershipLastSyncDto | null
  /** Super admin: the only role that can use the Manage dialog the skip line links to. */
  canEdit?: boolean
  /** What the brand connector holds today; a FAILED run stores no identity to read it from. */
  credentialKind?: PartnershipCredentialKind | null
}

const STATUS_DOT_CLS: Record<PartnershipDiscoveryStatus, string> = {
  ok: "bg-system-green-500",
  partial: "bg-system-yellow-500",
  unavailable: "bg-system-red-500",
  skipped: "bg-system-gray-500",
}

const STATUS_TEXT_CLS: Record<PartnershipDiscoveryStatus, string> = {
  ok: "text-system-gray-200",
  partial: "text-system-yellow-100",
  unavailable: "text-system-red-300",
  skipped: "text-system-gray-300",
}

/**
 * One warning: the fix-it text (the API's own classified sentence, or the
 * fingerprint map's for legacy/raw text) and, only when the API attached
 * Meta's own words as a `(Meta: …)` tail, that segment underneath — never
 * Marathon's own message re-labelled as Meta's.
 */
function WarningItem({
  raw,
  credentialKind,
}: {
  raw: string
  credentialKind: PartnershipCredentialKind | null
}) {
  const { metaNote } = splitMetaNote(raw)
  const explained = explainSyncWarning(raw, credentialKind)
  return (
    <li className="flex flex-col gap-0.5">
      <span title={explained !== raw ? raw : undefined}>{explained}</span>
      {metaNote && (
        <span
          className="text-system-gray-400"
          title="What Meta actually reported"
        >
          Meta said: {metaNote}
        </span>
      )}
    </li>
  )
}

export default function SyncHealthPanel({
  lastSync,
  canEdit = false,
  credentialKind = null,
}: SyncHealthPanelProps) {
  const [expanded, setExpanded] = useState(false)
  if (!lastSync) return null

  if (lastSync.status === "FAILED") {
    return (
      <section
        aria-label="Sync health"
        data-testid="sync-health"
        className="rounded-md border border-system-red-600 bg-system-red-900/30 px-system-md py-system-sm text-xs text-system-red-100"
      >
        <span className="font-medium">The last sync failed</span>
        {lastSync.error && (
          <span> — {explainSyncWarning(lastSync.error, credentialKind)}</span>
        )}
        <span>
          {" "}
          Fix the cause, then Refresh from Meta. Until then the table shows the
          previous sync&apos;s posts.
        </span>
      </section>
    )
  }

  if (lastSync.status !== "COMPLETED") return null

  // Deploy skew: an API that predates this contract sends no `warnings` /
  // `discovery` at all — degrade to "nothing to report" rather than crash.
  const discovery = lastSync.discovery ?? null
  const warnings = (lastSync.warnings ?? []).filter(
    w => !isInformationalSyncWarning(w),
  )
  if (!discovery && warnings.length === 0) return null

  // Meta-side flakiness (5xx after retries, a Meta rate limit, a refused
  // per-creator query) is listed so the user knows coverage may be
  // incomplete, but it is not theirs to fix: only actionable warnings, an
  // unavailable target, or a partial Instagram read that yielded nothing turn
  // the panel amber.
  const actionable = warnings.filter(w => !isMetaTransientSyncWarning(w))
  const hasProblem =
    actionable.length > 0 ||
    (discovery !== null &&
      (discovery.instagram.status === "unavailable" ||
        (discovery.instagram.status === "partial" &&
          discovery.instagram.items === 0) ||
        discovery.facebook.status === "unavailable"))
  const collapsed = warnings.length > COLLAPSE_WARNINGS_ABOVE && !expanded

  // "(via agency login Tom)" / "(via connector login)" — which credential the
  // discovery reads ran on. Null for identities stored before the hybrid.
  const credentialSuffix = discoveryCredentialSuffix(lastSync.identity)

  return (
    <section
      aria-label="Sync health"
      data-testid="sync-health"
      data-tone={hasProblem ? "warning" : "ok"}
      className={cn(
        "flex flex-col gap-1 rounded-md border px-system-md py-system-sm text-xs",
        hasProblem
          ? "border-system-yellow-600 bg-system-yellow-900/30 text-system-yellow-100"
          : "border-basic-blue bg-basic-dark-blue text-system-gray-200",
      )}
    >
      {discovery && (
        <ul className="flex flex-col gap-0.5">
          {(
            [
              ["Instagram", discovery.instagram],
              ["Facebook", discovery.facebook],
            ] as const
          ).map(([platform, outcome]) => (
            <li
              key={platform}
              className={cn(
                "flex items-start gap-2",
                STATUS_TEXT_CLS[outcome.status],
              )}
            >
              <span
                aria-hidden
                className={cn(
                  "mt-1 inline-block h-2 w-2 shrink-0 rounded-full",
                  STATUS_DOT_CLS[outcome.status],
                )}
              />
              <span>
                <span>{formatDiscoveryLine(platform, outcome)}</span>
                {/* Facebook's skipped line is the fixed "not enabled" sentence;
                    a credential suffix there would claim a read that never ran. */}
                {credentialSuffix &&
                  (platform === "Instagram" ||
                    outcome.status !== "skipped") && (
                    <span className="ml-1 text-system-gray-400">
                      {credentialSuffix}
                    </span>
                  )}
                {canEdit &&
                  platform === "Instagram" &&
                  outcome.status === "skipped" &&
                  isNoAgencyCredentialSkip(outcome.detail) && (
                    <a
                      href={`#${BUSINESS_MANAGER_PANEL_ID}`}
                      className="ml-1 font-medium underline hover:text-white"
                    >
                      Manage the discovery credential
                    </a>
                  )}
              </span>
            </li>
          ))}
        </ul>
      )}

      {warnings.length > 0 &&
        (collapsed ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="self-start font-medium text-system-yellow-100 hover:underline"
          >
            {warnings.length} warnings from the last sync — show them
          </button>
        ) : (
          <>
            <ul className="flex list-disc flex-col gap-1 ps-4">
              {warnings.map((raw, i) => (
                <WarningItem
                  key={`${i}-${raw.slice(0, 32)}`}
                  raw={raw}
                  credentialKind={lastSync.identity?.credentialKind ?? null}
                />
              ))}
            </ul>
            {warnings.length > COLLAPSE_WARNINGS_ABOVE && (
              <button
                type="button"
                onClick={() => setExpanded(false)}
                className="self-start text-system-gray-300 hover:underline"
              >
                Hide warnings
              </button>
            )}
          </>
        ))}
    </section>
  )
}
